import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Plus,
  Search,
  Edit2,
  Trash2,
  Filter,
  Globe,
} from 'lucide-react';
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Button,
  Badge,
  Modal,
  ModalFooter,
  Input,
  StatCard,
} from '@azyntor-labs/nexus-ui';
import { tenantsApi } from '@/api/endpoints';
import { Tenant } from '@/types';
import { format } from 'date-fns';
import { toast } from '@/utils/toast';

export const TenantManagement = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    is_active: true,
    settings: {
      max_users: 100,
      max_exams: 50,
      features: {
        proctoring: true,
        analytics: true,
        question_banks: true,
      },
    },
  });

  // Fetch tenants
  const { data: tenantsResponse, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantsApi.list(),
  });

  const tenants = tenantsResponse?.data.data || [];

  // Filter tenants by search term
  const filteredTenants = tenants.filter((tenant: Tenant) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      tenant.name.toLowerCase().includes(searchLower) ||
      tenant.domain.toLowerCase().includes(searchLower)
    );
  });

  // Calculate stats
  const activeTenants = tenants.filter((t: Tenant) => t.is_active).length;
  const inactiveTenants = tenants.filter((t: Tenant) => !t.is_active).length;

  // Create tenant mutation
  const createTenantMutation = useMutation({
    mutationFn: (data: Partial<Tenant>) => tenantsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant created successfully');
      setIsAddModalOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create tenant');
    },
  });

  // Update tenant mutation
  const updateTenantMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Tenant> }) =>
      tenantsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant updated successfully');
      setIsEditModalOpen(false);
      setSelectedTenant(null);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update tenant');
    },
  });

  // Delete tenant mutation
  const deleteTenantMutation = useMutation({
    mutationFn: (id: string) => tenantsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedTenant(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete tenant');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      domain: '',
      is_active: true,
      settings: {
        max_users: 100,
        max_exams: 50,
        features: {
          proctoring: true,
          analytics: true,
          question_banks: true,
        },
      },
    });
  };

  const handleAddTenant = () => {
    setIsAddModalOpen(true);
    resetForm();
  };

  const handleEditTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    const tenantSettings = tenant.settings as any;
    setFormData({
      name: tenant.name,
      domain: tenant.domain,
      is_active: tenant.is_active,
      settings: {
        max_users: tenantSettings?.max_users || 100,
        max_exams: tenantSettings?.max_exams || 50,
        features: {
          proctoring: tenantSettings?.features?.proctoring ?? true,
          analytics: tenantSettings?.features?.analytics ?? true,
          question_banks: tenantSettings?.features?.question_banks ?? true,
        },
      },
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.domain) {
      toast.error('Please fill in all required fields');
      return;
    }
    createTenantMutation.mutate(formData);
  };

  const handleSubmitUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenant) return;
    updateTenantMutation.mutate({ id: selectedTenant.id, data: formData });
  };

  const handleConfirmDelete = () => {
    if (!selectedTenant) return;
    deleteTenantMutation.mutate(selectedTenant.id);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Tenant Management</h1>
          <p className="text-white/50 mt-1">Manage organizations and their configurations</p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={handleAddTenant}>
          Add Tenant
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Tenants"
          value={tenants.length}
          icon={<Building2 />}
          color="blue"
        />
        <StatCard
          title="Active Tenants"
          value={activeTenants}
          icon={<Building2 />}
          trend="up"
          trendValue={tenants.length > 0 ? `${Math.round((activeTenants / tenants.length) * 100)}%` : undefined}
          color="green"
        />
        <StatCard
          title="Inactive Tenants"
          value={inactiveTenants}
          icon={<Building2 />}
          color="red"
        />
      </div>

      {/* Search */}
      <Card>
        <div className="p-4 border-b border-white/[0.06]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search tenants by name or domain..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center text-sm text-white/50">
              <Filter className="w-4 h-4 mr-2" />
              Showing {filteredTenants.length} of {tenants.length} tenants
            </div>
          </div>
        </div>

        {/* Tenants Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-white/50">Loading tenants...</p>
          </div>
        ) : filteredTenants.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeader>Domain</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Features</TableHeader>
                <TableHeader>Created</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTenants.map((tenant: Tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-4 h-4 text-white/40" />
                      <span className="font-medium text-white">{tenant.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Globe className="w-4 h-4 text-white/40" />
                      <span className="text-white/70">{tenant.domain}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={tenant.is_active ? 'success' : 'danger'} size="sm">
                      {tenant.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-1">
                      {tenant.settings?.features?.proctoring && (
                        <Badge variant="info" size="sm">Proctoring</Badge>
                      )}
                      {tenant.settings?.features?.analytics && (
                        <Badge variant="info" size="sm">Analytics</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-white/50">
                    {format(new Date(tenant.created_at), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Edit2 className="w-3 h-3" />}
                        onClick={() => handleEditTenant(tenant)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Trash2 className="w-3 h-3" />}
                        onClick={() => handleDeleteTenant(tenant)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-white/30 mx-auto mb-2" />
            <p className="text-white/50 mb-3">No tenants found</p>
            {searchTerm ? (
              <p className="text-sm text-white/40">Try adjusting your search</p>
            ) : (
              <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={handleAddTenant}>
                Add Your First Tenant
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Add Tenant Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Tenant"
        size="lg"
      >
        <form onSubmit={handleSubmitCreate}>
          <div className="space-y-4">
            <Input
              label="Organization Name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Acme University"
            />
            <Input
              label="Domain"
              required
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              placeholder="e.g., acme.edu"
              helperText="Unique domain for this tenant"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Max Users"
                type="number"
                value={formData.settings.max_users}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, max_users: parseInt(e.target.value) }
                })}
              />
              <Input
                label="Max Exams"
                type="number"
                value={formData.settings.max_exams}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, max_exams: parseInt(e.target.value) }
                })}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-white/70">Features</label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="proctoring"
                    checked={formData.settings.features.proctoring}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: {
                        ...formData.settings,
                        features: { ...formData.settings.features, proctoring: e.target.checked }
                      }
                    })}
                    className="w-4 h-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="proctoring" className="text-sm text-white/70">
                    Enable Proctoring
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="analytics"
                    checked={formData.settings.features.analytics}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: {
                        ...formData.settings,
                        features: { ...formData.settings.features, analytics: e.target.checked }
                      }
                    })}
                    className="w-4 h-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="analytics" className="text-sm text-white/70">
                    Enable Analytics
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="question_banks"
                    checked={formData.settings.features.question_banks}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: {
                        ...formData.settings,
                        features: { ...formData.settings.features, question_banks: e.target.checked }
                      }
                    })}
                    className="w-4 h-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="question_banks" className="text-sm text-white/70">
                    Enable Question Banks
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-white/70">
                Active
              </label>
            </div>
          </div>
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createTenantMutation.isPending}>
              {createTenantMutation.isPending ? 'Creating...' : 'Create Tenant'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Edit Tenant Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Tenant"
        size="lg"
      >
        <form onSubmit={handleSubmitUpdate}>
          <div className="space-y-4">
            <Input
              label="Organization Name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <Input
              label="Domain"
              required
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              helperText="Unique domain for this tenant"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Max Users"
                type="number"
                value={formData.settings.max_users}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, max_users: parseInt(e.target.value) }
                })}
              />
              <Input
                label="Max Exams"
                type="number"
                value={formData.settings.max_exams}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, max_exams: parseInt(e.target.value) }
                })}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-white/70">Features</label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit_proctoring"
                    checked={formData.settings.features.proctoring}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: {
                        ...formData.settings,
                        features: { ...formData.settings.features, proctoring: e.target.checked }
                      }
                    })}
                    className="w-4 h-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="edit_proctoring" className="text-sm text-white/70">
                    Enable Proctoring
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit_analytics"
                    checked={formData.settings.features.analytics}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: {
                        ...formData.settings,
                        features: { ...formData.settings.features, analytics: e.target.checked }
                      }
                    })}
                    className="w-4 h-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="edit_analytics" className="text-sm text-white/70">
                    Enable Analytics
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit_question_banks"
                    checked={formData.settings.features.question_banks}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: {
                        ...formData.settings,
                        features: { ...formData.settings.features, question_banks: e.target.checked }
                      }
                    })}
                    className="w-4 h-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="edit_question_banks" className="text-sm text-white/70">
                    Enable Question Banks
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit_is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500"
              />
              <label htmlFor="edit_is_active" className="text-sm font-medium text-white/70">
                Active
              </label>
            </div>
          </div>
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateTenantMutation.isPending}>
              {updateTenantMutation.isPending ? 'Updating...' : 'Update Tenant'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Tenant"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-white/70">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-white">{selectedTenant?.name}</span>? This action cannot be undone.
          </p>
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-400">
              Warning: All users, exams, and data associated with this tenant will be permanently deleted.
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setIsDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={deleteTenantMutation.isPending}
          >
            {deleteTenantMutation.isPending ? 'Deleting...' : 'Delete Tenant'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
